import React, { useContext, useEffect, useRef } from 'react'
import handleViewport from 'react-in-viewport'

import {
  EnterpriseContext,
  getSafeUrlParametersFromWindow,
  i18n,
  useLoginCheck,
  useSiteConfig,
  useSiteDefinition,
  useTransmit,
} from '@thg-commerce/enterprise-core'
import { getColumboEventData } from '@thg-commerce/enterprise-metrics'
import { pushToEnhancedDataLayer } from '@thg-commerce/enterprise-metrics/src/data_layer'
import { Review } from '@thg-commerce/enterprise-network/src/generated/graphql'
import { useTheme } from '@thg-commerce/enterprise-pages/src/Product/theme'
import { Separator } from '@thg-commerce/gravity-elements'
import { Grid } from '@thg-commerce/gravity-system'

import { ProductRating } from '../../ProductRating'
import { ProductReviewContent } from '../../ProductReviewContent'
import { NoReviewsFound } from '../NoReviewsFound'
import { ProductReviewSchema } from '../ProductReviewSchema'
import { DisclaimerText } from '../styles'
import {
  generateAddReviewFormUrlWithReturnTo,
  getReviewFiltersFromUrl,
} from '../utils'

import {
  IndividualReviews,
  IndividualReviewsGridItem,
  MainWrapper,
  ReviewOverview,
  ReviewsGrid,
  ReviewTitle,
  TopReviewsTitle,
} from './styles'

export const REVIEW_CARD_HEIGHT = 400 // px
export const REVIEWS_INITIALLY_RENDERED = 3

const TopReviewsTitleContainer = ({ forwardedRef, children }) => (
  <div ref={forwardedRef}>{children}</div>
)

const handleParams = (reviewFilters) => {
  const existingParams = { reviewFilters }
  if (typeof window !== 'undefined') {
    const params = getSafeUrlParametersFromWindow(window, '*')
    Object.assign(existingParams, params)
  }
  return existingParams
}

export const TopProductReviews = ({
  product,
  reviewFilters,
  enableAlternativeText,
}) => {
  const initialRender = useRef(true)
  const trackingEventRef = useRef(null)
  const isLoggedIn = useLoginCheck()
  const { originUrl } = useSiteDefinition()
  const {
    enableReviewFormFeatureRatings,
    productPageReviewsEnabled,
  } = useSiteConfig()
  const transmit = useTransmit()
  const theme = useTheme()
  const { subsite, siteId, channel } = useSiteDefinition()
  const propertyArgs = { subsite, siteId, channel }
  const EnterpriseCtx = useContext(EnterpriseContext)

  const i18nText = {
    customerReviewsTitle: i18n('reviews.customerreviews.title'),
    failedToFetch: i18n('review.content.failed.text'),
    customerReviewsDisclaimer: i18n('reviews.disclaimer.text'),
    topCustomerReviews: i18n('reviews.topcustomerreviews.title'),
    alternativeCustomerReviews: i18n(
      'reviews.topcustomerreviews.alternative.title',
    ),
  }

  useEffect(() => {
    const observer = new IntersectionObserver((elements, _) => {
      const targetElement = elements[0]
      if (
        initialRender.current &&
        targetElement &&
        targetElement.isIntersecting
      ) {
        initialRender.current = false
        pushToEnhancedDataLayer({
          event: 'review_viewed',
          component: 'ProductReviews component',
        })
      }
    })
    trackingEventRef.current && observer.observe(trackingEventRef.current)
  }, [])

  if (!product) {
    return null
  }

  const reviews = product.topProductReviews

  if (!reviews || (reviews.total === 0 && productPageReviewsEnabled)) {
    return <NoReviewsFound />
  }

  const reviewTitle = product.title || ''

  const reviewContent = reviews.reviews?.reviews?.map(
    (customerReview, index) => {
      const ProductReviewContentViewport = handleViewport(
        ProductReviewContent,
        {
          rootMargin: `${REVIEW_CARD_HEIGHT}px`,
        },
      )
      if (index < REVIEWS_INITIALLY_RENDERED) {
        return (
          <ProductReviewContent
            productTitle={reviewTitle}
            customerReview={customerReview}
            key={customerReview.id}
            sku={product.sku}
            showInlineFeatureScores={enableReviewFormFeatureRatings}
          />
        )
      }
      return (
        <ProductReviewContentViewport
          productTitle={reviewTitle}
          customerReview={customerReview as Review}
          key={customerReview.id}
          sku={product.sku}
          showInlineFeatureScores={enableReviewFormFeatureRatings}
        />
      )
    },
  )

  const showReviews = reviewContent && reviewContent.length > 0

  const existingParams = handleParams(reviewFilters)
  const formattedReviewFilters = getReviewFiltersFromUrl(
    existingParams.reviewFilters || '',
  )

  if (!productPageReviewsEnabled) {
    return null
  }

  const TopReviewsTitleInViewport = handleViewport(
    TopReviewsTitleContainer,
    {},
    { disconnectOnLeave: false },
  )

  const DisplayReviewTitle = () => {
    return (
      <>
        <ReviewTitle
          textAlignment={
            theme.pageTheme.topProductReviews.reviewTitle?.textAlignment
          }
          textStyle={theme.pageTheme.topProductReviews.reviewTitle?.textStyle}
          textTransform={
            theme.pageTheme.topProductReviews.reviewTitle?.textTransform ||
            'uppercase'
          }
          ref={trackingEventRef}
        >
          {i18nText.customerReviewsTitle}
        </ReviewTitle>
      </>
    )
  }

  return (
    <MainWrapper id="reviews">
      <ProductReviewSchema product={product} />
      <DisplayReviewTitle />
      <ReviewsGrid>
        <ReviewOverview
          colSpan={enableReviewFormFeatureRatings ? 12 : [12, 12, 4, 4]}
          colStart={1}
        >
          <ProductRating
            starRating={reviews.averageScore!}
            numberOfReviews={reviews.total!}
            count1Score={reviews.count1Score!}
            count2Score={reviews.count2Score!}
            count3Score={reviews.count3Score!}
            count4Score={reviews.count4Score!}
            count5Score={reviews.count5Score!}
            averageFeatureScores={reviews.featureScores}
            showInlineFeatureScores={enableReviewFormFeatureRatings}
            createReviewLink={generateAddReviewFormUrlWithReturnTo(
              product.sku,
              originUrl,
              isLoggedIn,
            )}
            viewAllReviewsLink={product.url?.replace(/.html$/, '.reviews')}
            filters={{ scores: formattedReviewFilters.scores }}
            url={product.url}
            showReviews={showReviews}
            enableFocusOutline={false}
          />
        </ReviewOverview>
        {showReviews && (
          <IndividualReviews
            colSpan={enableReviewFormFeatureRatings ? 12 : [12, 12, 7, 7]}
            colStart={enableReviewFormFeatureRatings ? 1 : [1, 1, 6, 6]}
          >
            <Grid>
              <IndividualReviewsGridItem
                colSpan={enableReviewFormFeatureRatings ? [12, 12, 8, 8] : 12}
                colStart={1}
                showInlineFeatureScores={enableReviewFormFeatureRatings}
              >
                {enableAlternativeText ? (
                  <TopReviewsTitleInViewport
                    onEnterViewport={() =>
                      transmit({
                        type: 'columbo',
                        payload: getColumboEventData({
                          propertyArgs,
                          argumentsObj: {
                            '0': 'Top customer reviews',
                          },
                          requestArgs: {
                            client_timestamp: Math.round(Date.now() / 1000),
                            url: window.location.href,
                          },
                          eventData: {
                            type: 'focus',
                            subtype: 'top_customer_reviews',
                            contents: [],
                          },
                          nonce: EnterpriseCtx.metricNonce,
                        }),
                      })
                    }
                  >
                    <TopReviewsTitle
                      textStyle={
                        theme.pageTheme.topProductReviews.topReviewTitle
                          ?.textStyle
                      }
                    >
                      {i18nText.alternativeCustomerReviews}
                    </TopReviewsTitle>
                  </TopReviewsTitleInViewport>
                ) : (
                  <TopReviewsTitle
                    textStyle={
                      theme.pageTheme.topProductReviews.topReviewTitle
                        ?.textStyle
                    }
                  >
                    {i18nText.topCustomerReviews}
                  </TopReviewsTitle>
                )}
                <DisclaimerText>
                  {i18nText.customerReviewsDisclaimer}
                </DisclaimerText>
                <Separator />
              </IndividualReviewsGridItem>
            </Grid>
            {reviewContent}
          </IndividualReviews>
        )}
      </ReviewsGrid>
      {!showReviews && <Separator />}
    </MainWrapper>
  )
}

export default TopProductReviews
